import { ArrowRight } from "lucide-react";
import { Link } from "react-router";
import Footer from "../components/Footer";
import Header from "../components/Header";
import SEO from "../components/SEO";
import { JOIN_FORM_URL } from "../config/links";
import { blogPosts } from "../data/blogPosts";

const infrastructureLayers = [
  {
    number: "01",
    name: "Data",
    title: "Resolve what records refer to",
    description:
      "Open tools for linking fragmented records, mapping overlooked places, and carrying evidence with data as it moves between systems.",
  },
  {
    number: "02",
    name: "Identity",
    title: "Verify without surrendering control",
    description:
      "Portable credentials, signatures, and privacy-preserving workflows that work across institutions, including offline and low-resource environments.",
  },
  {
    number: "03",
    name: "Intelligence",
    title: "Build systems that understand local context",
    description:
      "Language and representation infrastructure for records, products, and places that today's global models routinely misunderstand or leave out.",
  },
];

const workModes = [
  {
    title: "Research",
    description:
      "Long-horizon questions in data, identity, cryptography, AI, agents, world models, and public infrastructure.",
  },
  {
    title: "Open-source tools",
    description:
      "Libraries, datasets, and technical standards that others can inspect, adopt, and extend.",
  },
  {
    title: "Applied pilots",
    description:
      "Field-tested work with communities, institutions, and markets facing real data constraints.",
  },
  {
    title: "Education",
    description:
      "Programs, writing, and technical material that make difficult ideas easier to work with.",
  },
  {
    title: "Convening",
    description:
      "Meetups, seminars, and annual gatherings for engineers, researchers, designers, creatives, operators, and robotics builders.",
  },
  {
    title: "Incubation",
    description:
      "Support for ideas moving from subject-matter expertise and small teams into technology research.",
  },
];

const researchThreads = [
  {
    number: "01",
    title: "Privacy-preserving entity resolution",
    description:
      "Matching messy records across systems without exposing or centralizing sensitive data.",
  },
  {
    number: "02",
    title: "Ground-truth and open mapping infrastructure",
    description:
      "Representing places, roads, facilities, and local infrastructure through open and community-driven systems.",
  },
  {
    number: "03",
    title: "Product identity and provenance",
    description:
      "Resolving suppliers, facilities, and products into records that can carry evidence.",
  },
  {
    number: "04",
    title: "Verifiable credentials and signatures",
    description:
      "Portable claims that can be verified across institutions, including offline and low-resource environments.",
  },
  {
    number: "05",
    title: "Information extraction for Global South languages",
    description:
      "NLP and extraction methods for African languages, informal text, and multilingual records.",
  },
  {
    number: "06",
    title: "Benchmarking intelligence in context",
    description:
      "Evaluating customer-facing chatbots, AI agents, information quality, and entity resolution against the languages, ambiguity, risk, and operational constraints they encounter in practice.",
  },
  {
    number: "07",
    title: "World models, agents, and hyperdimensional computing",
    description:
      "Exploring HDC, embodied intelligence, robotics, and agent systems that can reason with messy signals, local context, and limited data.",
  },
];

const workingPrinciples = [
  {
    title: "Open by default",
    description:
      "Code, datasets, models, and standards are public, inspectable, and designed for reuse.",
  },
  {
    title: "Non-extractive by design",
    description:
      "The people closest to the problem retain agency over how infrastructure is built and governed.",
  },
  {
    title: "Grounded in real conditions",
    description:
      "We design for fragmented records, intermittent connectivity, multilingual contexts, and institutional constraints.",
  },
];

export default function Home() {
  const featuredPost = blogPosts.find((post) => post.featured) || blogPosts[0];

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Unpatterned | Research, infrastructure, and knowledge network"
        description="Unpatterned is an independent research, infrastructure, and knowledge network building open systems for how the world is represented in data, identity, and intelligence."
      />
      <Header />

      <main>
        {/* Hero */}
        <section className="px-6 pb-16 pt-32 md:pb-24 md:pt-40">
          <div className="mx-auto max-w-6xl">
            <div className="mb-8 flex items-center gap-4 font-['Manrope',sans-serif] text-xs font-semibold uppercase tracking-[0.18em] text-[#184b9d]">
              <span>Unpatterned</span>
              <span className="h-px w-12 bg-[#184b9d]/35" aria-hidden="true" />
              <span>Research · Infrastructure · Knowledge network</span>
            </div>

            <h1 className="max-w-5xl font-['DM_Serif_Display',serif] text-5xl leading-[0.98] text-[#050e1d] sm:text-6xl md:text-8xl">
              Open systems for how the world is represented.
            </h1>

            <div className="mt-12 grid gap-8 border-t border-[#050e1d]/15 pt-8 md:grid-cols-12 md:gap-10">
              <p className="font-['DM_Serif_Display',serif] text-2xl leading-snug text-[#050e1d] md:col-span-5 md:text-3xl">
                Unpatterned is an independent research, infrastructure, and
                knowledge network building open systems for data, identity, and
                intelligence.
              </p>

              <div className="md:col-span-6 md:col-start-7">
                <p className="max-w-2xl font-['Manrope',sans-serif] text-lg leading-relaxed text-[#050e1d]/70 md:text-xl">
                  We work across research, open-source tools, applied pilots,
                  education, and convening to help emerging markets build and
                  govern the technologies that will shape the next generation.
                </p>
                <p className="mt-5 max-w-2xl font-['Manrope',sans-serif] text-base leading-relaxed text-[#050e1d]/60">
                  Our first focus is non-extractive representation
                  infrastructure: systems that help communities, institutions,
                  and markets resolve, verify, and govern data about people,
                  places, products, and knowledge.
                </p>

                <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                  <Link
                    to="/labs"
                    className="inline-flex items-center justify-center gap-2 rounded-md bg-[#050e1d] px-6 py-3.5 font-['Manrope',sans-serif] font-semibold text-white transition-colors hover:bg-[#184b9d] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#184b9d]"
                  >
                    Explore the work
                    <ArrowRight size={17} aria-hidden="true" />
                  </Link>
                  <a
                    href={JOIN_FORM_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center rounded-md border border-[#050e1d]/25 px-6 py-3.5 font-['Manrope',sans-serif] font-semibold text-[#050e1d] transition-colors hover:border-[#050e1d] hover:bg-[#050e1d]/[0.03] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#184b9d]"
                  >
                    Work with us
                  </a>
                </div>
              </div>
            </div>

            <div className="mt-14 grid border-y border-[#050e1d]/10 sm:grid-cols-3">
              {["Research", "Labs", "Knowledge network"].map(
                (principle, index) => (
                  <div
                    key={principle}
                    className={`py-4 font-['Manrope',sans-serif] text-sm font-medium text-[#050e1d]/65 ${
                      index > 0
                        ? "border-t border-[#050e1d]/10 sm:border-l sm:border-t-0 sm:pl-6"
                        : ""
                    }`}
                  >
                    <span className="mr-3 font-mono text-xs text-[#184b9d]/60">
                      0{index + 1}
                    </span>
                    {principle}
                  </div>
                ),
              )}
            </div>
          </div>
        </section>

        {/* Operating lanes */}
        <section className="border-y border-[#050e1d]/10 px-6 py-20 md:py-24">
          <div className="mx-auto max-w-6xl">
            <div className="grid gap-6 md:grid-cols-12">
              <div className="md:col-span-5">
                <p className="font-['Manrope',sans-serif] text-xs font-semibold uppercase tracking-[0.18em] text-[#184b9d]">
                  What Unpatterned does
                </p>
                <h2 className="mt-5 font-['DM_Serif_Display',serif] text-4xl leading-tight text-[#050e1d] md:text-5xl">
                  A research institute, a lab, and a network in one system.
                </h2>
              </div>
              <p className="font-['Manrope',sans-serif] text-base leading-relaxed text-[#050e1d]/60 md:col-span-5 md:col-start-8 md:self-end">
                The long-term ambition is broader than one product: build the
                technical work, the people, and the shared knowledge needed for
                technology, design, and scientific advancement in emerging
                markets.
              </p>
            </div>

            <div className="mt-12 grid border-t border-[#050e1d]/15 md:grid-cols-3">
              {workModes.map((mode, index) => (
                <article
                  key={mode.title}
                  className={`border-b border-[#050e1d]/15 py-7 md:px-6 md:py-9 ${
                    index % 3 !== 0 ? "md:border-l" : ""
                  }`}
                >
                  <span className="font-mono text-xs text-[#184b9d]/55">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  <h3 className="mt-5 font-['Manrope',sans-serif] text-lg font-semibold text-[#050e1d]">
                    {mode.title}
                  </h3>
                  <p className="mt-2 font-['Manrope',sans-serif] text-sm leading-relaxed text-[#050e1d]/60">
                    {mode.description}
                  </p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Infrastructure layers */}
        <section className="bg-[#f7f8fa] px-6 py-20 md:py-28">
          <div className="mx-auto max-w-6xl">
            <div className="grid gap-10 lg:grid-cols-12 lg:gap-16">
              <div className="lg:col-span-4">
                <p className="mb-5 font-['Manrope',sans-serif] text-xs font-semibold uppercase tracking-[0.18em] text-[#184b9d]">
                  First focus
                </p>
                <h2 className="font-['DM_Serif_Display',serif] text-4xl leading-tight text-[#050e1d] md:text-5xl">
                  Representation infrastructure is the wedge.
                </h2>
                <p className="mt-6 font-['Manrope',sans-serif] text-base leading-relaxed text-[#050e1d]/65">
                  If a person, place, or product is missing or misrepresented
                  in the underlying data, every decision built on top of that
                  record inherits the error.
                </p>
              </div>

              <div className="border-t border-[#050e1d]/15 lg:col-span-8">
                {infrastructureLayers.map((layer) => (
                  <article
                    key={layer.name}
                    className="grid gap-4 border-b border-[#050e1d]/15 py-8 sm:grid-cols-[72px_1fr] md:py-10"
                  >
                    <div className="font-mono text-xs text-[#184b9d]/60">
                      {layer.number}
                    </div>
                    <div className="grid gap-3 md:grid-cols-[170px_1fr] md:gap-8">
                      <h3 className="font-['Manrope',sans-serif] text-sm font-semibold uppercase tracking-[0.12em] text-[#050e1d]">
                        {layer.name}
                      </h3>
                      <div>
                        <p className="font-['Manrope',sans-serif] text-xl font-semibold text-[#050e1d]">
                          {layer.title}
                        </p>
                        <p className="mt-2 font-['Manrope',sans-serif] text-base leading-relaxed text-[#050e1d]/60">
                          {layer.description}
                        </p>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Research threads */}
        <section className="px-6 py-20 md:py-28">
          <div className="mx-auto max-w-6xl">
            <div className="mb-12 grid gap-5 md:grid-cols-12">
              <h2 className="font-['DM_Serif_Display',serif] text-4xl leading-tight text-[#050e1d] md:col-span-7 md:text-5xl">
                Research Threads
              </h2>
              <p className="font-['Manrope',sans-serif] text-base leading-relaxed text-[#050e1d]/55 md:col-span-5 md:self-end">
                Long-horizon questions that connect our current tools to the
                public infrastructure people will rely on next.
              </p>
            </div>

            <div className="overflow-hidden rounded-2xl border border-[#050e1d]/12 bg-white">
              {researchThreads.map((thread, index) => (
                <article
                  key={thread.title}
                  className={`grid gap-4 px-6 py-8 transition-colors hover:bg-[#f7f8fa] sm:grid-cols-[52px_1fr] md:px-9 md:py-9 ${
                    index > 0 ? "border-t border-[#050e1d]/10" : ""
                  }`}
                >
                  <div className="pt-1 font-mono text-xs text-[#184b9d]/45">
                    {thread.number}
                  </div>
                  <div>
                    <h3 className="font-['Manrope',sans-serif] text-base font-semibold text-[#050e1d] md:text-lg">
                      {thread.title}
                    </h3>
                    <p className="mt-1.5 max-w-4xl font-['Manrope',sans-serif] text-sm leading-relaxed text-[#050e1d]/55 md:text-base">
                      {thread.description}
                    </p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Operating model */}
        <section className="bg-[#050e1d] px-6 py-20 text-white md:py-28">
          <div className="mx-auto max-w-6xl">
            <div className="grid gap-8 border-b border-white/15 pb-12 md:grid-cols-12">
              <p className="font-['Manrope',sans-serif] text-xs font-semibold uppercase tracking-[0.18em] text-[#8fb9ff] md:col-span-4">
                How we work
              </p>
              <h2 className="font-['DM_Serif_Display',serif] text-4xl leading-tight md:col-span-8 md:text-5xl">
                Infrastructure, research, and education should create local
                capacity, not permanent dependency.
              </h2>
            </div>

            <div className="grid md:grid-cols-3">
              {workingPrinciples.map((principle, index) => (
                <article
                  key={principle.title}
                  className={`py-9 md:px-8 md:py-12 ${
                    index > 0
                      ? "border-t border-white/15 md:border-l md:border-t-0"
                      : "md:pr-8"
                  }`}
                >
                  <span className="font-mono text-xs text-[#8fb9ff]/70">
                    0{index + 1}
                  </span>
                  <h3 className="mt-6 font-['Manrope',sans-serif] text-xl font-semibold">
                    {principle.title}
                  </h3>
                  <p className="mt-3 font-['Manrope',sans-serif] text-base leading-relaxed text-white/60">
                    {principle.description}
                  </p>
                </article>
              ))}
            </div>

            <p className="border-t border-white/15 pt-8 font-['Manrope',sans-serif] text-sm text-white/50">
              Supported by the OpenStreetMap Foundation. Built toward the next
              generation of public-interest technology.
            </p>
          </div>
        </section>

        {/* Latest update */}
        {featuredPost && (
          <section className="px-6 py-20 md:py-24">
            <div className="mx-auto max-w-6xl">
              <div className="grid gap-8 border-t border-[#050e1d]/15 pt-8 md:grid-cols-12">
                <div className="md:col-span-4">
                  <p className="font-['Manrope',sans-serif] text-xs font-semibold uppercase tracking-[0.18em] text-[#184b9d]">
                    Latest update
                  </p>
                  <p className="mt-3 font-['Manrope',sans-serif] text-sm text-[#050e1d]/45">
                    {new Date(featuredPost.date).toLocaleDateString("en-GB", {
                      day: "numeric",
                      month: "long",
                      year: "numeric",
                    })}
                  </p>
                </div>

                <Link
                  to={`/blog/${featuredPost.id}`}
                  className="group md:col-span-8"
                >
                  <h2 className="max-w-3xl font-['DM_Serif_Display',serif] text-3xl leading-tight text-[#050e1d] transition-colors group-hover:text-[#184b9d] md:text-4xl">
                    {featuredPost.title}
                  </h2>
                  <p className="mt-4 max-w-2xl font-['Manrope',sans-serif] text-base leading-relaxed text-[#050e1d]/60">
                    {featuredPost.excerpt}
                  </p>
                  <span className="mt-6 inline-flex items-center gap-2 font-['Manrope',sans-serif] text-sm font-semibold text-[#184b9d]">
                    Read the update
                    <ArrowRight
                      size={16}
                      className="transition-transform group-hover:translate-x-1"
                      aria-hidden="true"
                    />
                  </span>
                </Link>
              </div>
            </div>
          </section>
        )}

        {/* Final CTA */}
        <section className="bg-[#f2f6fc] px-6 py-20 md:py-28">
          <div className="mx-auto grid max-w-6xl gap-8 md:grid-cols-12">
            <h2 className="font-['DM_Serif_Display',serif] text-4xl leading-tight text-[#050e1d] md:col-span-7 md:text-6xl">
              Help build the systems, knowledge, and network that last.
            </h2>
            <div className="md:col-span-5 md:self-end">
              <p className="font-['Manrope',sans-serif] text-lg leading-relaxed text-[#050e1d]/65">
                We work with engineers, researchers, designers, creatives,
                institutions, and communities who want important technologies
                to remain open, useful, and locally governable.
              </p>
              <div className="mt-7 flex flex-col gap-3 sm:flex-row">
                <a
                  href={JOIN_FORM_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center rounded-md bg-[#184b9d] px-6 py-3.5 font-['Manrope',sans-serif] font-semibold text-white transition-colors hover:bg-[#050e1d] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#184b9d]"
                >
                  Join the community
                </a>
                <Link
                  to="/about"
                  className="inline-flex items-center justify-center rounded-md border border-[#050e1d]/20 px-6 py-3.5 font-['Manrope',sans-serif] font-semibold text-[#050e1d] transition-colors hover:border-[#050e1d] hover:bg-white/60 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#184b9d]"
                >
                  How we work
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
